// App.tsx - Multi-Tenant Router

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// Providers & Hooks
import { useAuth } from './modules/auth/AuthContext';

// Services
import { getCurrentTenantId } from './core/services/firebase/firestore';

// Modules - Static imports (named exports)
import { TenantBuilder } from './modules/tenant-builder/TenantBuilder';
import { WebsiteRouter } from './modules/website/WebsiteRouter';
import { Login } from './modules/auth/Login';
import { DirectoryRouter } from './modules/portal/DirectoryRouter';
import { ClaimProfilePage } from './modules/portal/pages/ClaimProfilePage';
import { OnboardingWizard } from './modules/onboarding/OnboardingWizard';

// Admin Components
import { AdminLayout } from './modules/admin/components/AdminLayout';
import { AdminDashboardPage } from './modules/admin/pages/AdminDashboardPage';
import { SiteBuilder } from './modules/admin/SiteBuilder/SiteBuilder';
import { UsersListPage } from './modules/admin/pages/UsersListPage';
import { TenantsListPage } from './modules/admin/pages/TenantsListPage';
import Bookings from './modules/admin/pages/Bookings';
import Clients from './modules/admin/pages/Clients';
import Gallery from './modules/admin/pages/Gallery';
import Financials from './modules/admin/pages/Financials';
import Settings from './modules/admin/pages/Settings';
import { DeveloperToolsPage } from './modules/admin/pages/DeveloperToolsPage';
import { SocialProfilePage } from './modules/admin/pages/SocialProfilePage';
import { FeedPage } from './modules/social/pages/FeedPage';
import { DomainManagementPage } from './modules/admin/pages/DomainManagementPage';
import { AccessHubManagerPage } from './modules/admin/pages/AccessHubManagerPage';
import { PublicLayout } from './modules/portal/components/PublicLayout';
import { AccessHubPage } from './modules/access/AccessHubPage';

import ScrollToTop from './components/ScrollToTop';

// Componente Wrapper para rotas protegidas
const ProtectedAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-brand-midnight text-white">
        <div className="w-12 h-12 border-4 border-brand-gold border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 font-black uppercase tracking-widest text-xs opacity-40">Carregando Sistema...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <AdminLayout>{children}</AdminLayout>;
};

function AppContent() {
  const tenantId = getCurrentTenantId();

  return (
    <Routes>
      {/* Central de Acesso - Página principal de seleção de logins */}
      <Route path="/app" element={<AccessHubPage mode="admin" />} />

      {/* Onboarding / Boas-vindas */}
      <Route path="/boas-vindas" element={<OnboardingWizard />} />
      
      {/* Rotas de Autenticação Globais */}
      <Route path="/login" element={<Login />} />
      
      {/* Fluxo de Reivindicação (Magic Link) - Global para facilitar acesso */}
      <Route path="/invite/:token" element={<ClaimProfilePage />} />

      {/* Rotas Administrativas Protegidas */}
      <Route 
        path="/admin/*" 
        element={
          <ProtectedAdminRoute>
            <Routes>
              <Route path="dashboard" element={<AdminDashboardPage />} />
              <Route path="users" element={<UsersListPage />} />
              <Route path="tenants" element={<TenantsListPage />} />
              <Route path="site-builder" element={<SiteBuilder />} />
              <Route path="bookings" element={<Bookings />} />
              <Route path="clients" element={<Clients />} />
              <Route path="gallery" element={<Gallery />} />
              <Route path="financials" element={<Financials />} />
              <Route path="settings" element={<Settings />} />
              <Route path="developer" element={<DeveloperToolsPage />} />
              <Route path="profile" element={<SocialProfilePage />} />
              <Route path="feed" element={<FeedPage />} />
              <Route path="domains" element={<DomainManagementPage />} />
              <Route path="access-hub" element={<AccessHubManagerPage />} />
              <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
            </Routes>
          </ProtectedAdminRoute>
        } 
      />

      {/* Rotas de Conteúdo Principal (Varia por TenantId) */}
      {tenantId === 'platform' ? (
         <Route path="/*" element={<PublicLayout><DirectoryRouter /></PublicLayout>} />
      ) : tenantId === 'builder' ? (
         <Route path="/builder/*" element={<TenantBuilder />} />
      ) : (
         <Route path="/*" element={<WebsiteRouter />} />
      )}

      {/* Fallbacks Adicionais */}
      {tenantId === 'builder' && <Route path="*" element={<Navigate to="/builder" replace />} />}
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <AppContent />
      </Router>
    </HelmetProvider>
  );
}

export default App;